<template>
  <div aria-hidden="true" class="OsmLayerHandler" style="display: none"></div>
</template>

<script setup lang="ts">
import type { Map as MaplibreMap } from 'maplibre-gl'
import maplibregl from 'maplibre-gl'
import MapComponent from '@/components/map/Map.vue'
import { useMyMapStore } from '@/stores/myMapStore'
import { MartinStyleService } from '@/services/map/MartinStyleService'
import { OsmClusterService } from '@/services/map/OsmClusterService'
import { OsmHeatmapService } from '@/services/map/OsmHeatmapService'
import { AtlasCategoriesService } from '@/services/map/osm/AtlasCategoriesService'
import { registerClassificationValueImage } from '@/services/map/TypeOuvrageMarkerService'
import { watch, onUnmounted } from 'vue'

const props = defineProps<{
  myMap: InstanceType<typeof MapComponent> | undefined
  mapInstance: MaplibreMap | null
}>()

const myMapStore = useMyMapStore()

/** Tracks which OSM style ids have been applied and their prefixed layer/source ids for removal. */
const appliedStylesCache = new globalThis.Map<
  string,
  { sourceIds: string[]; layerIds: string[] }
>()

/** styleId convention for per-typeOuvrage infrastructure vector tile layers (see AtlasCategoriesService). */
const INFRA_STYLE_PREFIX = 'infra_type_ouvrage_'

/**
 * Applies (or clears) the classification filter selected in the right panel to a style's live
 * (ghost) layers. The classification property (e.g. `classe`) is already flattened onto every
 * feature in the vector tile, so this is a pure client-side `setFilter` — no extra network call.
 * Note: this alone does not affect clustered rendering (see OsmClusterService), since clustering
 * reads raw features via querySourceFeatures and ignores layer filters — that path is handled
 * separately by applyClassificationColor (via OsmClusterService.refreshFilter / a full rebuild).
 */
function applyClassificationFilter(map: MaplibreMap, styleId: string, layerIds: string[]): void {
  const filter = myMapStore.osmClassificationFilters.get(styleId)
  const expr = filter ? (['in', ['get', filter.key], ['literal', filter.values]] as unknown as maplibregl.FilterSpecification) : null
  for (const id of layerIds) {
    if (!map.getLayer(id)) continue
    try {
      map.setFilter(id, expr)
    } catch {
      // Layer type may not support the property yet (e.g. mid style reload); safe to ignore.
    }
  }
}

/** Reads back the type d'ouvrage's own default icon/color/anchor, stashed on the layer's metadata
 *  when the style was first applied — see MartinStyleService.convertCirclePointLayersToPictoPins. */
function readDefaultIcon(
  map: MaplibreMap,
  layerIds: string[],
): { imageId: string; color: string; anchor: 'bottom' | 'center' } | undefined {
  for (const id of layerIds) {
    const layer = map.getLayer(id) as unknown as { metadata?: Record<string, unknown> } | undefined
    const defaultImageId = layer?.metadata?.['sigcom:defaultIconImageId']
    const defaultColor = layer?.metadata?.['sigcom:defaultIconColor']
    const defaultAnchor = layer?.metadata?.['sigcom:defaultIconAnchor']
    if (typeof defaultImageId === 'string' && typeof defaultColor === 'string') {
      return {
        imageId: defaultImageId,
        color: defaultColor,
        anchor: defaultAnchor === 'center' ? 'center' : 'bottom',
      }
    }
  }
  return undefined
}

/** Sets a layer's icon-image (static id or `match` expression) and matches the icon-anchor/size
 *  convention used when the picto pin was first built — see convertCirclePointLayersToPictoPins. */
function applyLayersIcon(
  map: MaplibreMap,
  layerIds: string[],
  iconImage: unknown,
  anchor: 'bottom' | 'center',
): void {
  for (const id of layerIds) {
    if (!map.getLayer(id)) continue
    try {
      map.setLayoutProperty(id, 'icon-image', iconImage)
      map.setLayoutProperty(id, 'icon-anchor', anchor)
      map.setLayoutProperty(id, 'icon-size', anchor === 'center' ? 0.65 : 0.5)
    } catch {
      // Layer may not be a symbol layer (e.g. line/fill) — nothing to recolor there.
    }
  }
}

/**
 * Recolors a type d'ouvrage's point layer(s) to match the classification value(s) currently
 * selected in the right panel (e.g. "Publique" -> blue) — the same coloring the classification
 * swatch already shows:
 *  - zero selected -> the type d'ouvrage's own default icon.
 *  - exactly one selected -> every (already filtered-to-match) picto takes that value's color.
 *  - several selected -> each picto keeps its OWN value's color (a `match` expression), same
 *    treatment line/polygon type d'ouvrages (routes, plan cadastral) already get natively from a
 *    backend `match` paint expression on their own vector style.
 * The cluster bubble follows the same rule — a flat color for zero/one, and its dominant category
 * for several (see OsmClusterService.refreshIcon / ClusterClassificationColoring).
 */
async function applyClassificationColor(map: MaplibreMap, styleId: string, layerIds: string[]): Promise<void> {
  // No INFRA_STYLE_PREFIX gate here: findMarkerConfigForTypeOuvrageStyleId matches on the type
  // d'ouvrage's own styleId regardless of naming convention (the backend may supply a custom one —
  // applyClassificationFilter above isn't gated either, for the same reason).
  const markerConfig = AtlasCategoriesService.findMarkerConfigForTypeOuvrageStyleId(styleId)
  const classification = markerConfig?.classifications?.[0]
  if (!classification) return

  const filter = myMapStore.osmClassificationFilters.get(styleId)
  const selectedValues = filter ? classification.values.filter((v) => filter.values.includes(v.value)) : []
  const isClustered = myMapStore.osmClusteredStyleIds.includes(styleId)
  const defaultIcon = readDefaultIcon(map, layerIds)

  if (selectedValues.length >= 2) {
    const valueImageIds: Array<[string, string]> = []
    let anchor: 'bottom' | 'center' = defaultIcon?.anchor ?? 'bottom'
    for (const value of selectedValues) {
      const registered = await registerClassificationValueImage(map, 'osm-picto-pin-mc', markerConfig, value)
      if (registered) {
        valueImageIds.push([value.value, registered.imageId])
        anchor = registered.anchor
      }
    }
    if (valueImageIds.length === 0) return

    const defaultIconImageId = defaultIcon?.imageId ?? valueImageIds[0][1]
    const matchExpr: unknown[] = ['match', ['get', classification.key]]
    for (const [value, imgId] of valueImageIds) matchExpr.push(value, imgId)
    matchExpr.push(defaultIconImageId)

    applyLayersIcon(map, layerIds, matchExpr, anchor)

    if (isClustered) {
      // Cluster bubbles can't reflect one flat color for a mix of categories: rebuild the cluster
      // wrapper with per-category counts so each bubble is colored by its dominant category —
      // `clusterProperties` can't be patched onto an already-created GeoJSON source.
      OsmClusterService.remove(map, styleId)
      OsmClusterService.setup(map, styleId, layerIds, {
        key: classification.key,
        values: selectedValues.map((v) => ({ value: v.value, color: v.color })),
      })
    }
    return
  }

  const selectedValue = selectedValues[0]
  let imageId: string | undefined
  let color: string | undefined
  let anchor: 'bottom' | 'center' = defaultIcon?.anchor ?? 'bottom'

  if (selectedValue) {
    const registered = await registerClassificationValueImage(map, 'osm-picto-pin-mc', markerConfig, selectedValue)
    if (registered) {
      imageId = registered.imageId
      color = registered.color
      anchor = registered.anchor
    }
  }

  if (!imageId) {
    // No value selected (or its image failed to register): restore the type d'ouvrage's own
    // default icon.
    imageId = defaultIcon?.imageId
    color = defaultIcon?.color
    anchor = defaultIcon?.anchor ?? anchor
  }

  if (!imageId) return

  applyLayersIcon(map, layerIds, imageId, anchor)

  if (isClustered) {
    // Cluster ignores layer filters (it reads raw source features), so the selection change
    // itself needs an explicit data refresh here, not just the color update below.
    OsmClusterService.refreshFilter(map, styleId)
    if (color) OsmClusterService.refreshIcon(map, styleId, imageId, color)
  }
}

/**
 * Checks whether the classification axis (classifications[0].key) is actually present on any
 * feature of the currently loaded tiles, and tallies a per-value feature count — recording both in
 * the store so the right panel can hide the Publique/Privée-style checkboxes when it would only
 * ever filter the map down to zero results (see ISSUE_DATA_CLASSIFICATIONS_TYPE_OUVRAGE.md: some
 * communes' schools were imported through a lot that never received a CATEGORIE value, unlike
 * Soa's), and show a count badge next to each value otherwise. Reuses the same source/sourceLayer
 * resolution as OsmClusterService.setup and retries once, since the initial querySourceFeatures
 * call can run before tiles finish decoding.
 */
function checkClassificationAvailability(map: MaplibreMap, styleId: string, layerIds: string[]): void {
  const markerConfig = AtlasCategoriesService.findMarkerConfigForTypeOuvrageStyleId(styleId)
  const classification = markerConfig?.classifications?.[0]
  if (!classification) return

  const resolveSource = (): { vectorSourceId: string; sourceLayer: string } | undefined => {
    const firstLayerId = layerIds.find((id) => map.getLayer(id))
    if (!firstLayerId) return undefined
    const layer = map.getLayer(firstLayerId) as { source?: string; sourceLayer?: string } | undefined
    const vectorSourceId = layer?.source
    if (!vectorSourceId || !map.getSource(vectorSourceId)) return undefined
    return { vectorSourceId, sourceLayer: layer?.sourceLayer ?? '' }
  }

  // Only writes to the store once the source's tiles are actually loaded: querying before that
  // returns an empty/partial feature set and would wrongly flip the checkbox list to
  // "unavailable" for a moment (and show a 0 count) before the real data lands right after —
  // the flicker this guard prevents.
  const doCheck = (): boolean => {
    const resolved = resolveSource()
    if (!resolved) return false
    const { vectorSourceId, sourceLayer } = resolved
    if (!map.isSourceLoaded(vectorSourceId)) return false
    try {
      const features = map.querySourceFeatures(vectorSourceId, {
        sourceLayer,
        filter: ['has', classification.key],
      })
      myMapStore.setOsmClassificationAvailability(styleId, features.length > 0)

      // querySourceFeatures can return the same feature twice across overlapping tile edges —
      // dedupe by a stable identity before tallying, same key strategy as OsmClusterService.
      const seen = new Set<string>()
      const counts = new globalThis.Map<string, number>()
      for (const feature of features) {
        const properties = (feature.properties ?? {}) as Record<string, unknown>
        const dedupeKey = String(properties.ift_id ?? properties.osm_id ?? feature.id ?? JSON.stringify(feature.geometry))
        if (seen.has(dedupeKey)) continue
        seen.add(dedupeKey)
        const value = properties[classification.key]
        if (typeof value !== 'string') continue
        counts.set(value, (counts.get(value) ?? 0) + 1)
      }
      myMapStore.setOsmClassificationValueCounts(styleId, counts)
      return true
    } catch {
      return false
    }
  }

  if (doCheck()) return

  // Tiles weren't loaded yet: wait for this source to report loaded instead of guessing on a
  // fixed delay, so the check runs exactly once the data is actually ready.
  const onSourceData = (e: maplibregl.MapSourceDataEvent) => {
    if (!e.isSourceLoaded) return
    if (doCheck()) map.off('sourcedata', onSourceData)
  }
  map.on('sourcedata', onSourceData)
  // Safety net in case 'sourcedata' never reports a fully-loaded state (e.g. already-cached tiles).
  setTimeout(() => {
    map.off('sourcedata', onSourceData)
    doCheck()
  }, 3000)
}

async function ensureOsmLayersSynced(): Promise<void> {
  const myMap = props.myMap
  const map = props.mapInstance
  if (!map || !myMap) return

  if (!map.loaded()) {
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => resolve(), 3000)
      map.once('idle', () => {
        clearTimeout(timeout)
        resolve()
      })
    })
  }

  const visibleIds = new Set(myMapStore.visibleOsmStyleIds)
  const toRemove = [...appliedStylesCache.keys()].filter((id) => !visibleIds.has(id))
  const toAdd = [...visibleIds].filter((id) => !appliedStylesCache.has(id))

  for (const styleId of toRemove) {
    const entry = appliedStylesCache.get(styleId)
    if (entry) {
      // Remove cluster layers BEFORE removing the underlying OSM style so the
      // hidden vector-tile layers are restored first (removeOsmStyleFromMap needs them).
      OsmHeatmapService.remove(map, styleId)
      myMapStore.removeOsmHeatmapStyleId(styleId)
      myMapStore.removeOsmClusteredStyleId(styleId)
      OsmClusterService.remove(map, styleId)
      removeOsmLayerIds(entry.layerIds)
      MartinStyleService.removeOsmStyleFromMap(myMap, entry.layerIds, entry.sourceIds)
      appliedStylesCache.delete(styleId)
      myMapStore.clearOsmAppliedStyleData(styleId)
      myMapStore.clearOsmClassificationAvailability(styleId)
      myMapStore.clearOsmClassificationValueCounts(styleId)
    }
  }

  for (const styleId of toAdd) {
    // Infra type-ouvrage layers are scoped to the locked commune's perimeter, so the sidebar
    // count and the points rendered on the map match exactly (see backend cmnCode contract).
    const cmnCode = styleId.startsWith(INFRA_STYLE_PREFIX) ? myMapStore.activeCommuneCode : undefined
    // Same marker_config already used for the right panel's swatch (see LayerStyleSwatch), so the
    // pins shown on the map match the icon configured for that type d'ouvrage.
    const markerConfig = styleId.startsWith(INFRA_STYLE_PREFIX)
      ? AtlasCategoriesService.findMarkerConfigForTypeOuvrageStyleId(styleId)
      : undefined
    const result = await MartinStyleService.applyOsmStyleToMap(myMap, styleId, cmnCode, markerConfig)
    if (!result) {
      // Style failed to load (e.g. 404 because the typeOuvrage was removed from the
      // référentiel). Flip the toggle back off instead of leaving a stuck "on" eye
      // that renders nothing on the map.
      myMapStore.markOsmStyleLoadFailed(styleId)
      continue
    }
    myMapStore.clearOsmStyleLoadFailed(styleId)
    appliedStylesCache.set(styleId, result)
    applyClassificationFilter(map, styleId, result.layerIds)
    checkClassificationAvailability(map, styleId, result.layerIds)

    // Extract the first relevant layer's style data from the (non-prefixed) cached
    // style and store it reactively so the sidebar swatches reflect the real style.
    const cachedStyle = MartinStyleService.getCachedStyle(styleId)
    if (cachedStyle?.layers) {
      const PREFERRED_ORDER: Array<'symbol' | 'fill' | 'line' | 'circle'> = ['symbol', 'fill', 'line', 'circle']
      const firstLayer = PREFERRED_ORDER.reduce<typeof cachedStyle.layers[number] | undefined>(
        (found, type) => found ?? cachedStyle.layers!.find((l) => l.type === type),
        undefined,
      )
      if (firstLayer) {
        const layout = 'layout' in firstLayer ? (firstLayer.layout as Record<string, unknown>) : {}
        const paint = 'paint' in firstLayer ? (firstLayer.paint as Record<string, unknown>) : {}
        const rawIconName = layout?.['icon-image']
        // Extract custom uploaded icon URL from symbol layer metadata (sigcom:mapIconContentUrl).
        const symbolLayer = cachedStyle.layers.find((l) => l.type === 'symbol')
        const symbolMeta = symbolLayer
          ? ((symbolLayer as unknown as Record<string, unknown>).metadata as Record<string, unknown> | undefined)
          : undefined
        const rawCustomIconUrl = symbolMeta?.['sigcom:mapIconContentUrl']
        myMapStore.setOsmAppliedStyleData(styleId, {
          iconName: typeof rawIconName === 'string' && rawIconName ? rawIconName : undefined,
          customIconUrl: typeof rawCustomIconUrl === 'string' && rawCustomIconUrl ? rawCustomIconUrl : undefined,
          paint,
          layout,
        })
      }
    }

    // Set up clustering for point/symbol layers; if successful, also register the
    // unclustered-points layer so hover and popup still work for individual features.
    const clustered = OsmClusterService.setup(map, styleId, result.layerIds)
    if (clustered) {
      myMapStore.addOsmClusteredStyleId(styleId)
      const interactiveIds = OsmClusterService.getInteractiveLayerIds(styleId)
      addOsmLayerIds(map, interactiveIds)
    } else {
      addOsmLayerIds(map, result.layerIds)
    }

    // A classification value may already be selected (e.g. re-applying after an admin style
    // refresh): recolor immediately instead of waiting for the next selection change.
    void applyClassificationColor(map, styleId, result.layerIds)
  }
}

let globalMouseMoveHandler: ((e: maplibregl.MapMouseEvent) => void) | null = null
let globalClickHandler: ((e: maplibregl.MapMouseEvent) => void) | null = null

/** All OSM layer IDs currently on the map (updated when layers are added/removed). */
const activeOsmLayerIds = new Set<string>()

const IGNORED_PROPERTIES = new Set(['category_id', 'subcategory_id'])
const HIT_TOLERANCE_PX = 10

function buildPopupDom(properties: Record<string, unknown>, geometryType: string): HTMLElement {
  const container = document.createElement('div')
  container.className = 'OsmLayerHandler__popup'

  const header = document.createElement('div')
  header.className = 'OsmLayerHandler__popupHeader'
  header.textContent = geometryType
  container.appendChild(header)

  const table = document.createElement('table')
  table.className = 'OsmLayerHandler__popupTable'

  const entries = Object.entries(properties)
    .filter(([key, val]) => !IGNORED_PROPERTIES.has(key) && val != null && String(val).trim() !== '')

  if (entries.length === 0) {
    const row = table.insertRow()
    row.insertCell().textContent = 'No properties'
  } else {
    for (const [key, val] of entries) {
      const row = table.insertRow()
      const labelCell = row.insertCell()
      labelCell.className = 'OsmLayerHandler__popupLabel'
      labelCell.textContent = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      const valueCell = row.insertCell()
      valueCell.className = 'OsmLayerHandler__popupValue'
      valueCell.textContent = String(val)
    }
  }

  container.appendChild(table)
  return container
}

function queryFeaturesNearPoint(map: MaplibreMap, point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
  const ids = [...activeOsmLayerIds].filter((id) => map.getLayer(id))
  if (ids.length === 0) return []
  const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
    [point.x - HIT_TOLERANCE_PX, point.y - HIT_TOLERANCE_PX],
    [point.x + HIT_TOLERANCE_PX, point.y + HIT_TOLERANCE_PX],
  ]
  return map.queryRenderedFeatures(bbox, { layers: ids })
}

function ensureGlobalHandlers(map: MaplibreMap) {
  if (globalMouseMoveHandler) return

  globalMouseMoveHandler = (e: maplibregl.MapMouseEvent) => {
    const features = queryFeaturesNearPoint(map, e.point)
    map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : ''
  }

  globalClickHandler = (e: maplibregl.MapMouseEvent) => {
    // Cluster bubble click: zoom to expansion level and stop further handling.
    if (OsmClusterService.handleClusterClick(map, e.point, e.lngLat)) return

    const features = queryFeaturesNearPoint(map, e.point)
    if (features.length === 0) return

    const feature = features[0]
    const myMap = props.myMap
    if (!myMap) return

    const properties = (feature.properties ?? {}) as Record<string, unknown>

    // Infrastructure feature (infra_type_ouvrage_* vector tile): open fiche modal instead of
    // a generic popup. Tile properties only carry ift_id/typ_id — detailed attributes are
    // fetched on demand by InfrastructureFicheContent via GET /api/infrastructures/{id}.
    if (properties.ift_id != null) {
      const iftId = Number(properties.ift_id)
      const iftIntitule =
        typeof properties.ift_intitule === 'string' ? properties.ift_intitule : String(iftId)
      const typId = typeof properties.typ_id === 'number' ? properties.typ_id : Number(properties.typ_id)
      const competenceId = Number.isFinite(typId)
        ? AtlasCategoriesService.findCompetenceIdForTypeOuvrage(typId)
        : undefined
      myMapStore.openInfrastructureFicheModal(iftId, iftIntitule, competenceId, properties)
      return
    }

    const popupDom = buildPopupDom(properties, feature.geometry?.type ?? 'unknown')
    myMap.addPopup(e.lngLat, popupDom, false)
  }

  map.on('mousemove', globalMouseMoveHandler as never)
  map.on('click', globalClickHandler as never)
}

function addOsmLayerIds(map: MaplibreMap, layerIds: string[]) {
  for (const id of layerIds) {
    if (map.getLayer(id)) activeOsmLayerIds.add(id)
  }
  ensureGlobalHandlers(map)
}

function removeOsmLayerIds(layerIds: string[]) {
  for (const id of layerIds) {
    activeOsmLayerIds.delete(id)
  }
}

function syncOsmHeatmapLayers(): void {
  const mapInstance = props.mapInstance
  if (!mapInstance) return
  const clustered = new Set(myMapStore.osmClusteredStyleIds)
  const heatmapOn = myMapStore.osmHeatmapStyleIds
  for (const styleId of clustered) {
    if (heatmapOn.has(styleId)) OsmHeatmapService.enable(mapInstance, styleId)
    else OsmHeatmapService.disable(mapInstance, styleId)
  }
  for (const styleId of heatmapOn) {
    if (!clustered.has(styleId)) OsmHeatmapService.disable(mapInstance, styleId)
  }
}

watch(
  [() => props.mapInstance, () => myMapStore.visibleOsmStyleIds],
  () => {
    void ensureOsmLayersSynced()
  },
  { immediate: true, deep: true },
)

// When the locked commune changes (entering/leaving the communal geoportail, or switching
// commune), force remove and re-apply any currently visible infra type-ouvrage layer so its
// vector tiles are re-fetched scoped to the new commune perimeter instead of showing stale
// (wrong-commune or national) points.
watch(
  () => myMapStore.activeCommuneCode,
  () => {
    for (const styleId of appliedStylesCache.keys()) {
      if (styleId.startsWith(INFRA_STYLE_PREFIX)) {
        myMapStore.requestOsmStyleRefresh(styleId)
      }
    }
  },
)

// When the admin saves a style, force remove and re-apply any currently visible
// layer so the map reflects the new paint/layout (label color, halo, anchor, etc.).
watch(
  () => myMapStore.pendingOsmStyleRefreshIds,
  (pendingIds) => {
    const myMap = props.myMap
    const map = props.mapInstance
    if (!map || !myMap || pendingIds.size === 0) return

    for (const styleId of pendingIds) {
      const entry = appliedStylesCache.get(styleId)
      if (!entry) {
        myMapStore.consumeOsmStyleRefresh(styleId)
        continue
      }
      // Remove the stale layer from the map and local cache so ensureOsmLayersSynced
      // will re-fetch (cache was already cleared by admin save) and re-apply.
      OsmHeatmapService.remove(map, styleId)
      myMapStore.removeOsmHeatmapStyleId(styleId)
      myMapStore.removeOsmClusteredStyleId(styleId)
      OsmClusterService.remove(map, styleId)
      removeOsmLayerIds(entry.layerIds)
      MartinStyleService.removeOsmStyleFromMap(myMap, entry.layerIds, entry.sourceIds)
      appliedStylesCache.delete(styleId)
      myMapStore.clearOsmAppliedStyleData(styleId)
      myMapStore.clearOsmClassificationAvailability(styleId)
      myMapStore.clearOsmClassificationValueCounts(styleId)
      myMapStore.consumeOsmStyleRefresh(styleId)
    }

    // Re-apply all layers that were just removed (they are still in visibleOsmStyleIds).
    void ensureOsmLayersSynced()
  },
)

watch(
  [
    () => props.mapInstance,
    () => [...myMapStore.osmClusteredStyleIds],
    () => [...myMapStore.osmHeatmapStyleIds].sort().join(','),
  ],
  () => {
    syncOsmHeatmapLayers()
  },
  { flush: 'post', immediate: true },
)

// Re-apply classification filters (and matching pin/cluster color) when the user (un)checks a
// class in the right panel.
watch(
  () => new globalThis.Map(myMapStore.osmClassificationFilters),
  () => {
    const map = props.mapInstance
    if (!map) return
    for (const [styleId, entry] of appliedStylesCache) {
      applyClassificationFilter(map, styleId, entry.layerIds)
      // Owns the clustered-refresh for classified styleIds itself (color update + data refresh,
      // or a full cluster rebuild for a multi-category selection — see applyClassificationColor).
      void applyClassificationColor(map, styleId, entry.layerIds)
    }
  },
  { deep: true },
)

onUnmounted(() => {
  const myMap = props.myMap
  const map = props.mapInstance
  if (map) {
    if (globalMouseMoveHandler) {
      map.off('mousemove', globalMouseMoveHandler as never)
      globalMouseMoveHandler = null
    }
    if (globalClickHandler) {
      map.off('click', globalClickHandler as never)
      globalClickHandler = null
    }
    OsmHeatmapService.teardownAll(map)
    OsmClusterService.teardownAll(map)
  }
  activeOsmLayerIds.clear()
  for (const [, entry] of appliedStylesCache) {
    if (myMap) {
      MartinStyleService.removeOsmStyleFromMap(myMap, entry.layerIds, entry.sourceIds)
    }
  }
  appliedStylesCache.clear()
})
</script>

<style lang="scss">
.OsmLayerHandler__popup {
  font-size: 0.8125rem;
  max-width: 20rem;
  max-height: 16rem;
  overflow: auto;
  padding: 0.75rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.12);
}

.OsmLayerHandler__popupHeader {
  font-size: 0.6875rem;
  color: #888;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.OsmLayerHandler__popupTable {
  border-collapse: collapse;
  width: 100%;
}

.OsmLayerHandler__popupLabel {
  font-weight: 600;
  padding: 0.2rem 0.5rem 0.2rem 0;
  white-space: nowrap;
  color: var(--color-main, #005959);
  vertical-align: top;
}

.OsmLayerHandler__popupValue {
  padding: 0.2rem 0;
  word-break: break-word;
}
</style>
